import { useEffect, useRef, useState } from 'react';
import { animate } from 'framer-motion';

interface CounterProps {
  value: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  format?: (value: number) => string;
  className?: string;
}

export function Counter({
  value,
  duration = 1.4,
  prefix = '',
  suffix = '',
  format,
  className,
}: CounterProps) {
  // Start at the target value so a static snapshot is never "0".
  // The animation immediately tweens from 0 back through to value
  // on mount; if it never runs (low-end device, blocked timer), the
  // user still sees the correct final number.
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    setDisplay(0);
    const controls = animate(0, value, {
      duration,
      ease: [0.2, 0.8, 0.2, 1],
      onUpdate: (v) => setDisplay(v),
      onComplete: () => setDisplay(value),
    });
    return () => controls.stop();
  }, [value, duration]);

  const rendered = format ? format(display) : Math.round(display).toString();

  return (
    <span className={className}>
      {prefix}
      {rendered}
      {suffix}
    </span>
  );
}

interface LiveCounterProps {
  value: number;
  format?: (value: number) => string;
  className?: string;
}

/**
 * For values that change after mount (e.g. calculator outputs). Tweens whenever
 * `value` changes.
 */
export function LiveCounter({ value, format, className }: LiveCounterProps) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);

  useEffect(() => {
    const from = prev.current;
    prev.current = value;
    const controls = animate(from, value, {
      duration: 0.45,
      ease: [0.2, 0.8, 0.2, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [value]);

  const rendered = format ? format(display) : Math.round(display).toString();

  return <span className={className}>{rendered}</span>;
}
